
import { Component, Prop, Vue, Emit, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { ActionPlanTask } from "@/store/tasks/types";
import { stringMixin } from "@/mixins";
import { validationMixin } from "vuelidate";
import { requiredIf } from "vuelidate/lib/validators";
import { FeaturesList } from "@/store/types";
import { get } from "lodash";

const { isNullOrWhitespace } = stringMixin.methods;

@Component({
	components: {},
	mixins: [validationMixin],
	validations: {
		inputTaskModel: {
			required: requiredIf(function () {
				return this.task.required;
			})
		}
	}
})

export default class InputTask extends Vue {
	@Prop(Boolean)
	public editing: Boolean;

	@Prop()
	public task!: ActionPlanTask;

	@Getter("getFeaturesList") featuresList: FeaturesList;

	public inputTaskModel: string = "";
	public hasValidationErrors: boolean = false;
	public showErrorAnimation: boolean = false;

	private readonly maxExpandedHeight: number = 133;
	private isEditing: boolean = false;
	private textAreaElement = null;

	public mounted(): void {
		if (this.editing) {
			this.onValidateTask();
		}

		this.setTextAreaMaxHeight();
	}

	public updated(): void {
		this.setTextAreaMaxHeight();
		this.handleTextAreaInput(null, true);
	}

	@Watch("task", { deep: true })
	public updateInputTaskModel(): void {
		// Update inputTaskModel when task is updated and field is not being edited
		if (!this.isEditing && !!this.task && this.task.result) {
			this.inputTaskModel = this.task.result;
			this.setTextAreaMaxHeight();
		}
	}

	@Emit("complete-task")
	public onCompleteTask() {
		this.isEditing = false;
		return {
			...this.task,
			result: this.inputTaskModel,
			completed: true
		};
	}

	// Ensure the task was setup correctly.
	@Emit("validate-task")
	public onValidateTask() {
		let isValid = !isNullOrWhitespace(this.task.taskText);

		if (!isValid) {
			this.hasValidationErrors = true;
			this.showErrorAnimation = true;

			setTimeout(() => {
				this.showErrorAnimation = false;
			}, this.$config.ANIMATION_DURATION);
		}

		return isValid;
	}

	private get isScalableTextBoxFeatureEnabled(): boolean {
		return get(this.featuresList, ["Actions", "ScalableTextBox"], false);
	}

	private setTextAreaMaxHeight(): void {
		let targetClasses : string = ".auto-resize-textarea" + ".task-" + this.task.taskID;
		this.textAreaElement = document.querySelector(targetClasses) as HTMLTextAreaElement;

		if (this.textAreaElement) {
			this.textAreaElement.style.maxHeight = `${this.maxExpandedHeight}px`;
		}
	}

	private handleTextAreaInput(event: Event, useTextAreaElementVariable : boolean = false): void {
		this.isEditing = true;

		// take the <textarea> as a variable from the event target, so we can alter its values
		let textarea = event ? event.target as HTMLTextAreaElement : null;

		// Use previously stored textAreaElement variable if boolean is true (used for setting height when a task is re-opened)
		if (useTextAreaElementVariable) textarea = this.textAreaElement;

		if (!textarea) {
			return;
		}

		// Setting height to 'auto' before scrollHeight leads to smoother animations between height values
		textarea.style.maxHeight = `${this.maxExpandedHeight}px`;
		textarea.style.height = "auto";
		textarea.style.height = `${textarea.scrollHeight}px`;

		// If the text content exceeds 5 lines (px value stored in this.maxExpandedHeight) then show the scrollbar
		textarea.style.overflowY = textarea.scrollHeight > this.maxExpandedHeight ? "auto" : "hidden";
	}
}
